import React, { forwardRef, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import * as Icons from '@polestar/component-warehouse-react/tokens/icons/Icons';
import { AnimatePresence, m as motion } from 'framer-motion';
import { createTransition } from '@polestar/component-warehouse-react/tokens/motion/Motion';
import { useInteractionMode } from '@polestar/component-warehouse-react/contexts/interactionMode';
import Spacer from '@polestar/component-warehouse-react/components/spacer/Spacer';
import Spacings, { createCustomSpacing, } from '@polestar/component-warehouse-react/tokens/spacings/Spacings';
import { useFocusGroup, useFocusInitial, useFocusRestore, } from '@polestar/component-warehouse-react/hooks/focus';
import useCombinedRef from '@polestar/component-warehouse-react/hooks/useCombinedRef';
import useDirectionStyle from '@polestar/component-warehouse-react/hooks/useDirectionStyle';
import { ButtonVariants, InnerContent, PrimaryButton, } from '@polestar/component-warehouse-react/components/button/Button';
import Icon from '@polestar/component-warehouse-react/components/icon/Icon';
import { overflowEllipsis } from '@polestar/component-warehouse-react/helpers/style';
import aria from '@polestar/component-warehouse-react/helpers/accessibility/aria';
import { shouldNotForwardProps } from '@polestar/component-warehouse-react/helpers/shouldForwardProps';
import { useReducedMotion } from '@polestar/component-warehouse-react/hooks/useReducedMotion';
import Opacities from '@polestar/component-warehouse-react/tokens/opacities/Opacities';
const transition = createTransition({
    reduced: true,
});
const disabledTransition = {
    type: false,
    delay: 0,
};
const ContextMenu = forwardRef(({ backButtonLabel = 'Back', items, ...props }, ref) => {
    const [menus, setMenus] = useState([items]);
    const { isKeyboardControlled } = useInteractionMode();
    const isReducedMotion = useReducedMotion();
    const isDisabledMotion = isKeyboardControlled || isReducedMotion;
    useEffect(() => {
        const hasIcons = items.filter((item) => (item.icon ? true : false));
        if (hasIcons.length > 0 && hasIcons.length !== items.length) {
            // eslint-disable-next-line no-console
            console.warn('[ContextMenu] Please provide icons with ALL items in the ContextMenu and not just with:', hasIcons.map((item) => `"${item.label}"`).join(', '));
        }
        setMenus([items]);
    }, [items]);
    const handleSelect = useCallback((index) => {
        setMenus((items) => {
            var _a, _b;
            const selectedMenu = (_b = (_a = items[items.length - 1]) === null || _a === void 0 ? void 0 : _a[index]) === null || _b === void 0 ? void 0 : _b.items;
            return selectedMenu ? [...items, selectedMenu] : items;
        });
    }, []);
    const handleBack = useCallback((e) => {
        e === null || e === void 0 ? void 0 : e.stopPropagation();
        setMenus((items) => (items.length === 1 ? [items[0]] : items.slice(0, items.length - 1)));
    }, []);
    const [menuHeights, setMenuHeights] = useState([]);
    const currentHeight = menuHeights[menus.length - 1];
    const heightsChanged = menuHeights.length > menus.length;
    useEffect(() => {
        if (heightsChanged) {
            setMenuHeights((prevHeights) => prevHeights.slice(0, menus.length));
        }
    }, [menus.length, heightsChanged]);
    const handleHeightChange = useCallback((index, columnHeight) => {
        setMenuHeights((prevHeights) => {
            const newHeights = [...prevHeights];
            newHeights[index] = columnHeight;
            return newHeights;
        });
    }, []);
    const menuElements = menus.map((menuItem, index) => {
        const currentMenu = index === menus.length - 1;
        return (React.createElement(MenuColumn, { key: index, index: index, isKeyboardControlled: isKeyboardControlled, currentMenu: currentMenu, menuItem: menuItem, onBack: handleBack, backButtonLabel: backButtonLabel, onSelect: handleSelect, onHeightChange: handleHeightChange }));
    });
    const innerXValue = -useDirectionStyle((menus.length - 1) * 100);
    return (React.createElement(Container, { ref: ref, isKeyboardControlled: isKeyboardControlled, ...props, ...aria({
            role: 'menu',
        }) },
        React.createElement(Inner, { transition: isDisabledMotion ? disabledTransition : transition, initial: false, animate: {
                x: !isKeyboardControlled ? `${innerXValue}%` : 0,
                height: currentHeight ? currentHeight : 'auto',
            } }, !isKeyboardControlled ? (
        // We only need animation when using mouse, otherwise just render the items
        React.createElement(AnimatePresence, { initial: false }, menuElements)) : (menuElements))));
});
ContextMenu.displayName = 'ContextMenu';
export default ContextMenu;
const MenuColumn = forwardRef(({ index, isKeyboardControlled, menuItem, currentMenu, onBack, backButtonLabel, onSelect, onHeightChange, ...props }, ref) => {
    const internalRef = useRef();
    const heightRef = useCallback((node) => {
        if (node) {
            onHeightChange === null || onHeightChange === void 0 ? void 0 : onHeightChange(index, node.scrollHeight);
        }
    }, [index, onHeightChange]);
    const combinedRef = useCombinedRef(internalRef, ref, heightRef);
    const handleKeyDown = useCallback((event) => {
        if (event.key === 'ArrowRight') {
            const index = parseInt(event.currentTarget.dataset.index || '0');
            onSelect === null || onSelect === void 0 ? void 0 : onSelect(index);
        }
        else if (event.key === 'ArrowLeft') {
            onBack();
        }
    }, [onBack, onSelect]);
    const { createFocusProps } = useFocusGroup({
        ref: internalRef,
        initialFocusIndex: 0,
        onKeyDown: handleKeyDown,
    });
    useFocusRestore(isKeyboardControlled);
    useFocusInitial(internalRef, index > 0 && isKeyboardControlled);
    return (React.createElement(MenuColumnContainer, { ref: combinedRef, as: isKeyboardControlled ? 'div' : motion.div, ...(!isKeyboardControlled
            ? {
                initial: {
                    visibility: 'hidden',
                },
                animate: {
                    visibility: !currentMenu && isKeyboardControlled ? 'hidden' : 'visible',
                    pointerEvents: currentMenu ? 'auto' : 'none',
                },
                exit: {
                    visibility: !currentMenu && isKeyboardControlled ? 'hidden' : 'visible',
                    opacity: 0.9,
                },
                transition,
            }
            : {}), style: isKeyboardControlled && !currentMenu
            ? {
                display: 'none',
            }
            : {}, ...props },
        index > 0 && (React.createElement(Fragment, null,
            React.createElement(ContextMenuItem, { icon: Icons.chevronLeft, onClick: onBack, label: backButtonLabel, index: -1, ...createFocusProps(-1), ...(!currentMenu ? { tabIndex: -1 } : {}) }),
            React.createElement(ContextMenuDivider, null))),
        menuItem.map((item, index) => (React.createElement(Fragment, { key: `context-menu-item-${index}` },
            index === 0 && React.createElement(Spacer, { spacing: Spacings.xSmall }),
            React.createElement(ContextMenuItem, { index: index, ...item, onSelect: onSelect, hasSubMenu: !!item.items, ...createFocusProps(index), ...(!currentMenu ? { tabIndex: -1 } : {}), ...aria({
                    role: 'menuitem',
                    haspopup: !!item.items || undefined,
                    expanded: !!item.items ? !currentMenu : undefined,
                }) }),
            index === menuItem.length - 1 && React.createElement(Spacer, { spacing: Spacings.xSmall }))))));
});
MenuColumn.displayName = 'MenuColumn';
const ContextMenuItem = forwardRef(({ onClick, onSelect, label, index = 0, hasSubMenu, icon, color, active = false, ...props }, ref) => {
    const handleClick = useCallback((event) => {
        // Don't propagate on clicks when navigating to submenu
        if (hasSubMenu) {
            event.stopPropagation();
        }
        onSelect === null || onSelect === void 0 ? void 0 : onSelect(index);
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [index, onClick, onSelect, hasSubMenu]);
    const button = (React.createElement(StyledButton, { ref: ref, ...props, color: color, stretch: true, onClick: handleClick, active: active, iconAnimation: false },
        React.createElement(InnerContent, { variant: ButtonVariants.primary },
            icon && (React.createElement(StyledIcon, { "data-type": "icon", icon: icon, color: color, pairedWithText: true, isBackButton: hasSubMenu === undefined })),
            React.createElement(ButtonLabel, null, label),
            hasSubMenu && React.createElement(Icon, { icon: Icons.chevronRight }))));
    // ensure that custom overrides are past onto the inverted theme below
    return button;
});
ContextMenuItem.displayName = 'ContextMenuItem';
const Container = styled(motion.div, shouldNotForwardProps(false, 'isKeyboardControlled'))(({ isKeyboardControlled, theme }) => css `
    position: relative;
    border: 1px solid ${theme.contextMenu.borderColor};
    min-width: 15rem;
    overflow: ${isKeyboardControlled ? 'visible' : 'hidden'};
    background-color: ${theme.contextMenu.backgroundColor};
  `);
Container.displayName = `Container`;
const Inner = styled(motion.div) `
  display: flex;
  flex-direction: row;
  align-items: flex-start;
`;
const MenuColumnContainer = styled.div `
  display: flex;
  width: 100%;
  flex-shrink: 0;
  flex-grow: 0;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
`;
MenuColumnContainer.displayName = `MenuColumnContainer`;
const ContextMenuDivider = styled.div(({ theme }) => css `
    height: 1px;
    background-color: ${theme.contextMenu.borderColor};
    width: 100%;
  `);
const StyledButton = styled(PrimaryButton)(({ active, color, theme, }) => {
    const { enabled: enabledTheme, hover: hoverTheme, disabled: disabledTheme, active: activeTheme, } = theme.contextMenu.button.states;
    return css(css `
      padding-inline: ${Spacings.medium};
      background-color: ${enabledTheme.backgroundColor};
      color: ${enabledTheme.color};

      [data-type='icon'] {
        color: ${color || enabledTheme.iconColor};
      }

      &:hover:not(:disabled) {
        background-color: ${hoverTheme.backgroundColor};
        color: ${hoverTheme.color};

        [data-type='icon'] {
          color: ${color || hoverTheme.iconColor};
        }
      }

      &:disabled {
        background-color: ${disabledTheme.backgroundColor};
        color: ${disabledTheme.color};

        [data-type='icon'] {
          color: ${color || disabledTheme.iconColor};
          opacity: ${Opacities.low};
        }
      }
    `, active &&
        css `
        pointer-events: none;
        background-color: ${activeTheme.backgroundColor};
        color: ${activeTheme.color};

        [data-type='icon'] {
          color: ${color || activeTheme.iconColor};
        }
      `);
});
const StyledIcon = styled(Icon)(({ isBackButton }) => css(css `
      margin-inline-start: 0;
      margin-inline-end: ${createCustomSpacing(10 / 16)};
    `, !isBackButton &&
    css `
        min-width: ${createCustomSpacing(24 / 16)};
      `));
const ButtonLabel = styled.span `
  text-align: start;
  white-space: nowrap;
  ${overflowEllipsis};
  flex: 1;
`;
